/**
 * `VirtualList` — the windowing list the crate is built on.
 * `docs/design-direction.md` §8.2 ("Virtualise the list"), §10.2.
 *
 * The channel's catalogue is thousands of rows and the target device is a 2019
 * mid-range Android inside Telegram's webview, where the frame budget is 22ms and a
 * 5000-node ledger loses it before a finger has moved. So only the rows inside the
 * scrollport (plus a small overscan) are in the DOM at any moment.
 *
 * ## Padding spacers, not absolute positioning
 *
 * The window is expressed as **padding on the inner element** — `padding-block-start`
 * equal to the height of everything skipped above, `padding-block-end` to everything
 * skipped below — rather than by absolutely positioning each row at its offset. Two
 * reasons, both load-bearing for this app:
 *
 *  1. **`position: sticky` keeps working.** §8.2 puts a 48px sticky section header in
 *     the middle of the ledger, and a sticky element inside an absolutely-positioned
 *     row has nothing to stick to. Rows in normal flow stick correctly.
 *  2. **Rows keep their own box.** A `TrackRow` is a grid that sizes itself; pinning
 *     it to a computed `top` would mean this component owning row geometry it cannot
 *     see, and every row height would have to be exact rather than merely honest.
 *
 * The cost is that heights are declared, not measured: a caller hands each row the
 * height its CSS gives it (56px for a track row, 48px for a section header, 88px for
 * a flyer). That is a deliberate trade — the crate's rows are fixed-height by design
 * (§5.6: "Ledger rows hold 56px regardless of how little is in them"), and a
 * measuring list would need a `ResizeObserver` per row, which is exactly the kind of
 * per-row machinery §10.2 bans from a virtualised ledger.
 *
 * ## The scrollport is found, not owned
 *
 * `Shell` establishes **exactly one** scroll container for the whole app so the dock
 * can overlay it and sticky headers have a scrollport (`app/Shell.tsx`). A list that
 * opened its own `overflow-y: auto` would nest a second scroller inside the first —
 * two scrollbars, a broken sticky header, and Telegram's sheet-dismiss gesture
 * fighting the inner one. So this component **scrolls nothing**: it walks up to the
 * nearest scrollable ancestor, listens to it, and windows against it.
 *
 * Its own offset inside that scroller is measured once per content change rather than
 * on every scroll event — the value cannot change while scrolling, and re-reading a
 * rect in a scroll handler is a forced synchronous layout on the one code path that
 * must never take one.
 *
 * ## `container-type: inline-size`
 *
 * The wrapper is a size-query container (§5.2): the ledger's BPM column is **dropped
 * below 360px, never squeezed**, and it is the *column* width that decides that, not
 * the viewport — the app column caps at 480px and can sit beside chrome. Written as
 * an inline style rather than a class because the container context belongs to the
 * windowing wrapper itself, wherever a screen mounts one, and a caller that forgot to
 * pass the class would get a container query that silently never matches.
 *
 * `container-type: inline-size` computes to `contain: layout style inline-size`. That
 * is deliberately **not** `contain: paint`: paint containment would clip the crate's
 * edge-cropped stamps and would create a new scroll-clip that sticky headers cannot
 * escape.
 *
 * There is no `requestAnimationFrame` here and no `will-change` (§10.2.4, §12.20): a
 * scroll listener that only ever calls `setState` when the *window* changes is
 * already cheap, and React batches the rest.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, JSX, ReactNode } from 'react';

/* ══ model ═════════════════════════════════════════════════════════════════════════ */

/**
 * One row of the window. `height` is the height the row's CSS gives it, in pixels;
 * `key` is React's key and must be stable across renders for the same row.
 */
export interface VirtualRow {
  readonly key: string;
  readonly height: number;
}

/** A half-open window `[start, end)` over the row array. */
export interface VisibleRange {
  readonly start: number;
  readonly end: number;
}

/** Rows kept above and below the scrollport, so a flick never shows a gap. */
export const DEFAULT_OVERSCAN = 4;

/**
 * The viewport height used before the scrollport can be measured.
 *
 * A first render happens before any effect has run, and in a test environment there
 * is no layout at all, so `clientHeight` is `0`. Windowing to a zero-height viewport
 * would render nothing and then flash a full window in, so the fallback is the
 * window's own height where there is one — a real number, never a guess dressed up as
 * a measurement.
 */
export function fallbackViewportHeight(): number {
  if (typeof window === 'undefined') return 640;
  const height = window.innerHeight;
  return Number.isFinite(height) && height > 0 ? height : 640;
}

/* ══ geometry ══════════════════════════════════════════════════════════════════════ */

/**
 * Prefix sums of the row heights: `offsets[i]` is the top of row `i`, and the last
 * entry is the total height. Length is `rows.length + 1`.
 */
export function rowOffsets(rows: readonly VirtualRow[]): number[] {
  const offsets: number[] = new Array<number>(rows.length + 1);
  offsets[0] = 0;
  let accumulated = 0;
  for (let index = 0; index < rows.length; index += 1) {
    const height = rows[index]?.height ?? 0;
    accumulated += Number.isFinite(height) && height > 0 ? height : 0;
    offsets[index + 1] = accumulated;
  }
  return offsets;
}

/** The index of the row containing `y`, clamped to the list. */
function indexAt(offsets: readonly number[], y: number): number {
  let low = 0;
  let high = offsets.length - 2;
  if (high <= 0) return 0;
  while (low < high) {
    const mid = (low + high + 1) >> 1;
    if ((offsets[mid] ?? 0) <= y) low = mid;
    else high = mid - 1;
  }
  return low;
}

/**
 * The window covering `[top, bottom)` of the list's own coordinate space, widened by
 * `overscan` rows at each end.
 */
export function rangeFor(
  offsets: readonly number[],
  top: number,
  bottom: number,
  overscan: number,
): VisibleRange {
  const count = Math.max(0, offsets.length - 1);
  if (count === 0) return { start: 0, end: 0 };

  const first = indexAt(offsets, Math.max(0, top));
  const last = indexAt(offsets, Math.max(0, bottom));
  const pad = Math.max(0, Math.trunc(overscan));

  return {
    start: Math.max(0, first - pad),
    end: Math.min(count, last + 1 + pad),
  };
}

/** The nearest scrollable ancestor, or the document scroller. */
function resolvePort(node: HTMLElement): HTMLElement | null {
  let parent: HTMLElement | null = node.parentElement;
  while (parent !== null) {
    const overflowY = getComputedStyle(parent).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return parent;
    parent = parent.parentElement;
  }
  const scroller = document.scrollingElement ?? document.documentElement;
  return scroller instanceof HTMLElement ? scroller : null;
}

/** Where scroll events for `port` are actually delivered. */
function scrollTargetFor(port: HTMLElement): EventTarget {
  return port === (document.scrollingElement ?? document.documentElement) ? window : port;
}

/** The list's top, in the scroller's content coordinates. */
function measureOffset(port: HTMLElement, node: HTMLElement): number {
  const offset = node.getBoundingClientRect().top - port.getBoundingClientRect().top;
  const value = offset + port.scrollTop;
  return Number.isFinite(value) ? value : 0;
}

/* ══ component ═════════════════════════════════════════════════════════════════════ */

export interface VirtualListProps<T extends VirtualRow> {
  /** Every row of the list, in print order — including dividers and headers. */
  readonly rows: readonly T[];
  /** Renders one row. Called only for rows inside the window. */
  readonly renderRow: (row: T, index: number) => ReactNode;
  /** Rows kept above and below the scrollport. Default {@link DEFAULT_OVERSCAN}. */
  readonly overscan?: number;
  /**
   * The scroller to window against. Omitted, the nearest scrollable ancestor is
   * found — which is `Shell`'s single scroll container in the running app.
   */
  readonly scrollPort?: HTMLElement | null | undefined;
  /** Notified whenever the window moves, so a screen can page in more rows. */
  readonly onRangeChange?: ((range: VisibleRange) => void) | undefined;
  /** The inner element's tag. `ul` when the caller renders `li` rows. */
  readonly as?: 'div' | 'ul' | 'ol';
  readonly className?: string | undefined;
  readonly innerClassName?: string | undefined;
  /** Accessible name for the list element. */
  readonly ariaLabel?: string | undefined;
}

export function VirtualList<T extends VirtualRow>({
  rows,
  renderRow,
  overscan = DEFAULT_OVERSCAN,
  scrollPort,
  onRangeChange,
  as = 'div',
  className,
  innerClassName,
  ariaLabel,
}: VirtualListProps<T>): JSX.Element {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const portRef = useRef<HTMLElement | null>(null);
  const offsetRef = useRef<number>(0);

  const offsets = useMemo(() => rowOffsets(rows), [rows]);
  const totalHeight = offsets[offsets.length - 1] ?? 0;

  const [range, setRange] = useState<VisibleRange>(() =>
    rangeFor(offsets, 0, fallbackViewportHeight(), overscan),
  );

  /** Recompute the window from the live scroll position. */
  const recompute = useCallback((): void => {
    const port = portRef.current;
    const viewport =
      port === null || port.clientHeight <= 0 ? fallbackViewportHeight() : port.clientHeight;
    const scrollTop = port?.scrollTop ?? 0;
    const top = scrollTop - offsetRef.current;
    const next = rangeFor(offsets, top, top + viewport, overscan);
    setRange((previous) =>
      previous.start === next.start && previous.end === next.end ? previous : next,
    );
  }, [offsets, overscan]);

  /* Subscribe to the scroller. Re-runs when the content height changes, which is also
     when this list's offset inside the scroller can have moved. */
  useEffect(() => {
    const outer = outerRef.current;
    if (outer === null) return;

    const port = scrollPort ?? resolvePort(outer);
    portRef.current = port;
    if (port === null) {
      recompute();
      return;
    }

    offsetRef.current = measureOffset(port, outer);
    recompute();

    const handle = (): void => {
      recompute();
    };
    const target = scrollTargetFor(port);
    target.addEventListener('scroll', handle, { passive: true });
    window.addEventListener('resize', handle);

    return () => {
      target.removeEventListener('scroll', handle);
      window.removeEventListener('resize', handle);
    };
  }, [recompute, scrollPort, totalHeight]);

  /* Clamp at render: `rows` can shrink between a scroll and the next paint. */
  const count = rows.length;
  const start = Math.min(Math.max(0, range.start), count);
  const end = Math.min(Math.max(start, range.end), count);

  useEffect(() => {
    onRangeChange?.({ start, end });
  }, [onRangeChange, start, end]);

  const padStart = offsets[start] ?? 0;
  const padEnd = Math.max(0, totalHeight - (offsets[end] ?? totalHeight));

  const Inner = as;

  /*
   * The size-query container. Inline, because the container context belongs to the
   * windowing wrapper wherever it is mounted (see the header), and `contain` is not
   * spelled out separately: `container-type: inline-size` already computes to
   * `contain: layout style inline-size`, and adding `paint` would clip the crate's
   * edge-cropped print.
   */
  const outerStyle: CSSProperties = { containerType: 'inline-size' };

  const innerStyle: CSSProperties = {
    paddingBlockStart: `${String(padStart)}px`,
    paddingBlockEnd: `${String(padEnd)}px`,
  };

  return (
    <div
      ref={outerRef}
      className={className}
      style={outerStyle}
      data-virtual="true"
      data-rows={String(count)}
      data-window={`${String(start)}-${String(end)}`}
    >
      <Inner className={innerClassName} style={innerStyle} aria-label={ariaLabel}>
        {/* The key lives on a Fragment rather than on whatever the caller returns:
            the row's identity belongs to the list, and a Fragment adds no DOM node to
            a ledger whose `<ul>` may only contain `<li>` children. */}
        {rows.slice(start, end).map((row, offset) => (
          <Fragment key={row.key}>{renderRow(row, start + offset)}</Fragment>
        ))}
      </Inner>
    </div>
  );
}
